<i18n>
{
  "en": {
    "productSearchTitle": "Select a product",
    "getProductError": "Error getting product information",
    "typeToSearch": "Type to search",
    "emptyFieldError": "Mandatory field"
  },
  "es": {
    "productSearchTitle": "Selecciona un producto",
    "getProductError": "Error obteniendo información del producto",
    "typeToSearch": "Escribe para buscar",
    "emptyFieldError": "Campo obligatorio"
  }
}
</i18n>

<template>
  <v-dialog @click:outside="onCancel" v-model="visible" max-width="600px" scrollable persistent>
    <v-card v-if=visible height="100%" class="px-5 py-4">
      <div class="d-flex align-center justify-space-between">
        <v-card-title class="pa-0">{{ $t('productSearchTitle') }}</v-card-title>
        <transition name="progress">
          <v-progress-circular v-if="isLoading" indeterminate color="primary" class="mr-2"/>
        </transition>
      </div>
      <v-container class="pa-0">
        <v-form v-model="valid" ref="form">
          <dynameat-autocomplete v-model="selectedProduct" :items="availableProductsList"
                                 :rules="objectRules"
                                 :filter="productTextFilter" :label="$t('typeToSearch')"
                                 prepend-icon="mdi-database-search" chips persistent-hint
                                 return-object :required="true">
            <template v-slot:item="data">
              <template>
                <v-list-item-avatar>
                  <v-img :src="data.item.image" :alt="data.item.name" :lazy-src="defaultProductImg"
                         contain/>
                </v-list-item-avatar>
                <v-list-item-content class="h-scroll">
                  <v-list-item-title class="no-wrap">
                    {{ data.item.commercial_name }}
                    (
                    {{currency.hasLeftPosition() ? currency.symbol : ''}}
                    {{ data.item.first_price }}
                    {{currency.hasRightPosition() ? currency.symbol : ''}}
                    )
                  </v-list-item-title>
                  <v-list-item-subtitle class="no-wrap">
                    {{ data.item.name }} ({{ data.item.id_external }})
                  </v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </template>
            <template v-slot:selection="data">
              <v-chip class="caption | d-flex justify-center align-center"
                      close @click:close="onRemoveSelection(data.index)" @click.stop>
                <div class="chip-ellipsis">{{ data.item.name }}</div>
              </v-chip>
            </template>
          </dynameat-autocomplete>
          <dynameat-modal-buttons @cancel="onCancel" @confirm="onConfirm" :confirmDisabled="!valid"
                                  :confirmText="$t('confirm')"/>
        </v-form>
      </v-container>
    </v-card>
  </v-dialog>
</template>

<script>

import { notEmptyObject } from '@core/helpers/formValidators';
import { createErrorNotification } from '@ulibs/unnotificationsqueue';
import { productRequest } from '@pointOfSale/api';
import { Product } from '@dynameat/views/pointOfSale/models';
import Component from 'vue-class-component';
import {
  Components, MapActions, MapGetters, Mixins
} from '@core/helpers/decorators';
import { Prop, Watch } from 'vue-property-decorator';
import Vue from 'vue';
import DynameatModalButtons from '@dynameat/components/DynameatModalButtons';
import DynameatAutocomplete from '@dynameat/components/DynameatAutocomplete';
import { productMixins } from '@pointOfSale/mixins';

@Component
@Components({ DynameatModalButtons, DynameatAutocomplete })
@Mixins([productMixins])
@MapActions(['addNotification'])
@MapGetters(['masterProductList', 'editableProductList', 'masterProductListCancelToken', 'currency'])
export default class ProductSearchModal extends Vue {
  @Prop({ type: Boolean, default: false }) visible;

  valid = false;

  selectedProduct = null;

  progressValue = null;

  created() {
    this.selectedProduct = undefined;
  }

  get isLoading() {
    return this.masterProductListCancelToken !== null;
  }

  get objectRules() {
    return [notEmptyObject(this.$t('emptyFieldError'))];
  }

  get defaultProductImg() {
    // eslint-disable-next-line global-require
    return require('@dynameat/assets/images/dynameat-logo-secondary.png');
  }

  @Watch('product')
  onUpdatedVisible(updatedVisible) {
    if (updatedVisible) this.selectedProduct = undefined;
  }

  async onConfirm() {
    if (this.$refs.form.validate()) {
      try {
        const product = await this.getProduct(this.selectedProduct.guid);
        this.selectedProduct = undefined;
        this.$emit('confirm', product);
      } catch (e) {
        this.addNotification(createErrorNotification(this.$t('getProductError')));
        this.onCancel();
      }
    }
  }

  onCancel() {
    this.selectedProduct = undefined;
    this.$emit('cancel');
  }

  async getProduct(productGuid) {
    const response = await productRequest(productGuid);
    return Product.toInstance(response.data);
  }

  onRemoveSelection() {
    this.selectedProduct = undefined;
  }

  productTextFilter(product, queryText) {
    const searchText = queryText.toLowerCase();
    const name = product.name.toLowerCase();
    const commercialNameAlt1 = product.commercial_name_alt1 ? product.commercial_name_alt1.toLowerCase() : '';
    const commercialNameAlt2 = product.commercial_name_alt2 ? product.commercial_name_alt2.toLowerCase() : '';
    const commercialNameAlt3 = product.commercial_name_alt3 ? product.commercial_name_alt3.toLowerCase() : '';
    return name.indexOf(searchText) > -1
      || commercialNameAlt1.indexOf(searchText) > -1
      || commercialNameAlt2.indexOf(searchText) > -1
      || commercialNameAlt3.indexOf(searchText) > -1
      || String(product.id_external).indexOf(searchText) > -1;
  }
}
</script>
<style scoped>
.progress-enter-active, .progress-leave-active {
  transition: opacity .5s
}

.progress-enter, .progress-leave-to /* .fade-leave-active below version 2.1.8 */
{
  opacity: 0
}

.chip-ellipsis {
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}

.h-scroll {
  overflow-y: hidden;
  overflow-x: auto;
}

.no-wrap {
  text-overflow: initial;
  overflow: initial;
  white-space: nowrap;
}
</style>
